<script lang="ts">
  import type ArchieML from '$locales/en/content.json';

  interface Props {
    /* Whether the app is being rendered in an embed */
    embedded?: boolean;
    /* ArchieML story content */
    content: (typeof ArchieML)['story'];
    /* Locale for the page */
    locale?: string;
  }

  let { embedded = false, content }: Props = $props();

  import { asset } from '$app/paths';

  import {
    Article,
    Analytics,
    BodyText,
    Block,
    EndNotes,
    SiteHeadline,
    GraphicBlock,
    InlineAd,
  } from '@reuters-graphics/graphics-components';
  import LogBlock from './components/dev/LogBlock.svelte';
  import { containerWidth, inlineAdNumber } from '$utils/propValidators';
  import { isReutersDotcom } from '$utils/env';
  import { page } from '$app/state';
  import pkg from '$pkg';

  // Import ai2svelte components...
  import AiMap from './ai2svelte/ai-chart.svelte';

  // ...and add them to this object.
  const aiCharts = {
    AiMap,
  };

  import PollChart from './components/PollChart/index.svelte';
  import ElectionFlow from './components/HistoricalElections/ElectionFlow.svelte';
  import SmartText from './components/SmartText/index.svelte';
  import PMCandidates from './components/PMCandidates/index.svelte';
  import ElectionComparison from './components/ElectionComparison/index.svelte';
  import { loadLocalData, returnStructuredData } from '$lib/data/fetch';
  import { calculateSmartVars } from '$lib/utils';
  import scrapeMetadata from '$lib/data/scrape-metadata.json';
  import { max } from 'd3-array';
  import { timeFormat } from 'd3-time-format';

  const allPolls = loadLocalData();
  const structuredData = returnStructuredData(allPolls);
  const smartVars = calculateSmartVars(structuredData);
  // Most recent poll date across every party — drives the "Data as of"
  // note shown below both the poll chart and the seat-comparison chart.
  const latestPollDate = max(structuredData.data, (d) => d.date);

  const formatDisplayDate = timeFormat('%B %-d');

  // Fills in the `{date}` token in any end note's text (e.g., "Data as of
  // {date}.") with the latest poll date, same token used elsewhere on the page.
  const endNotes = $derived(
    content.endNotes.map((note) => ({
      ...note,
      text:
        latestPollDate ? note.text.replace('{date}', formatDisplayDate(latestPollDate)) : note.text,
    }))
  );
</script>

{#if !embedded && isReutersDotcom(page.url)}
  <Analytics authors={pkg?.reuters?.graphic?.authors || []} />
{/if}

<Article>
  <!--
    This component and others are part of our components library.
    📚 Read the docs: https://reuters-graphics.github.io/graphics-components/
  -->
  <SiteHeadline
    hed={content.hed}
    section={content.section}
    sectionUrl={content.sectionUrl}
    authors={content.authors}
    publishTime={content.publishTime}
    updateTime={scrapeMetadata.generated_at}
    class="two-line-hed"
  />

  <!-- 🔁 Looping through your ArchieML doc blocks... -->
  {#each content.blocks as block}
    <!-- Text block -->
    {#if block.type === 'text'}
      <BodyText text={block.text} />
    {:else if block.type === 'smart-text'}
      <Block>
        <SmartText {block} {smartVars} />
      </Block>
    {:else if block.type === 'graphic' && block.id === 'poll-chart'}
      <Block width="wider" class="fmb-4">
        <PollChart {block} {structuredData} {latestPollDate} />
      </Block>

      <!-- PM candidates -->
    {:else if block.type === 'PMCandiates'}
      <Block width="wide" class="fmb-4">
        <PMCandidates
          title={block.title}
          subHead={block.subHead}
          candidates={block.candidates}
          notes={block.notes}
          source={block.source}
          altText={block.altText}
        />
      </Block>

      <!-- Historical elections seat-flow chart -->
    {:else if block.type === 'HistoricalElections'}
      <Block width="wide" class="fmb-4">
        <ElectionFlow
          title={block.title}
          dek={block.subHead}
          source={block.source}
          notes={block.notes}
          altText={block.altText}
          sortOrder="desc"
        />
      </Block>

      <!-- Estimated v. 2022 seats comparison -->
    {:else if block.type === 'ElectionComparison'}
      <Block width="wide" class="fmb-4">
        <ElectionComparison
          title={block.title}
          subHead={block.subHead}
          notes={block.notes}
          unassignedNote={block.unassignedNote}
          source={block.source}
          dataAsOf={block.dataAsOf}
          altText={block.altText}
          {structuredData}
          {latestPollDate}
        />
      </Block>

      <!-- Ai2svelte graphic block -->
    {:else if block.type === 'ai-graphic'}
      {#if !aiCharts[block.chart]}
        <LogBlock message={`Unable to find "${block.chart}" in aiCharts`} />
      {:else}
        {@const AiChart = aiCharts[block.chart]}
        <GraphicBlock
          id={block.chart}
          width={containerWidth(block.width)}
          textWidth={containerWidth(block.textWidth)}
          title={block.title}
          description={block.description}
          notes={block.notes}
          ariaDescription={block.altText}
        >
          <AiChart assetsPath={asset('/')} />
        </GraphicBlock>
      {/if}

      <!-- Inline ad -->
    {:else if block.type === 'inline-ad'}
      {#if isReutersDotcom(page.url)}
        <InlineAd n={inlineAdNumber(block.n)} />
      {:else}
        <LogBlock level="info" message="An ad will appear here on dotcom" />
      {/if}

      <!-- Warning block -->
    {:else}
      <LogBlock message={`Unknown block type: "${block.type}"`} />
    {/if}
  {/each}

  <EndNotes notes={endNotes} />
</Article>

<style>
  :global(.two-line-hed h1) {
    white-space: pre-line;
  }
</style>
